'use client';

import { parse } from 'date-fns';
import { css } from '@bts-web/utils-style-engine';
import { formatDateToYmd } from '@bts-web/utils-formatting';
import { SavingsPlanFrequency } from '@bts-web/data-layer/server';
import { DatePicker } from '../../../../../common';
import {
  getYmdForNthDayFromToday,
  getYmdForCurrentMonthLengthAndNthDays,
} from '../../../utils/dateUtils';
import { SavingsPlanDateConfigItem } from './components/SavingsPlanDateConfigItem';
import {
  WeekDayPicker,
  getWeekDayNameFromYmd,
} from './components/WeekDayPicker';
import { Locale } from '@bts-web/utils-lokalise';
import { RegularClockPlusOutlineAlt } from '../../../../../common/svgIcons';
import { useModalDrawerContext } from '../../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { useIsBelowScreenWidth } from '@bts-web/core-features/layouts';
import { FrequencySelectDrawer } from './components/FrequencySelectDrawer';
import { getAppConfig } from '@bts-web/core-app-config';
import {
  SP_WEEKLY_FREQUENCY,
  SP_BIWEEKLY_FREQUENCY,
  SP_DAILY_FREQUENCY,
} from '../../types';

const YMD_STORAGE_FORMAT = 'yyyy-MM-dd';

export const SetUpSavingsPlanBox = ({
  translations,
  recurrenceDate,
  locale,
  onChangeDate,
  showFrequencyDropdown,
  frequencyOptions,
  frequencyValue,
  onFrequencyChange,
}: {
  translations: {
    setUpSavingsPlanBoxTitle: string;
    setUpSavingsPlanBoxDescription: string;
    frequency: string;
    startsAt: string;
    monthly: string;
    biweekly?: string;
    weekly: string;
    daily?: string;
    ok: string;
    cancel: string;
    nextRecurrence?: string;
  };
  recurrenceDate: string;
  locale: Locale;
  onChangeDate: (date: string) => void;
  showFrequencyDropdown: boolean;
  frequencyOptions?: SavingsPlanFrequency[];
  frequencyValue: SavingsPlanFrequency;
  onFrequencyChange: (freq: SavingsPlanFrequency) => void;
}) => {
  const {
    minimumCalendarDaysBeforeExecution = 4,
    maxDaysFromMonthEndForSavingsExecution = 2,
  } = getAppConfig();

  const { openDrawer } = useModalDrawerContext();

  const isMobile = useIsBelowScreenWidth(1024);

  const frequencyLabelMap: Partial<Record<SavingsPlanFrequency, string>> = {
    MONTHLY: translations.monthly,
    WEEKLY: translations.weekly,
    ...(translations.biweekly ? { BIWEEKLY: translations.biweekly } : {}),
    ...(translations.daily ? { DAILY: translations.daily } : {}),
  };

  const currentFrequencyLabel = frequencyValue
    ? frequencyLabelMap[frequencyValue]
    : translations.monthly;

  const validFrequencyOptions = frequencyOptions ?? [];

  const handleFrequencyRowClick = () => {
    openDrawer(FrequencySelectDrawer, {
      position: isMobile ? 'bottom' : 'right',
      frequencyOptions: validFrequencyOptions,
      currentSelectedFrequency: frequencyValue,
      onSelect: onFrequencyChange,
      translations: {
        MONTHLY: translations.monthly,
        WEEKLY: translations.weekly,
        ...(translations.biweekly ? { BIWEEKLY: translations.biweekly } : {}),
        ...(translations.daily ? { DAILY: translations.daily } : {}),
        frequency: translations.frequency,
        nextRecurrence: translations.nextRecurrence,
      },
      currentSelectedRecurrenceDate: recurrenceDate,
    });
  };

  return (
    <div
      data-testid="set-up-savings-plan-box"
      className={css({
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'neutrals.card_fill_primary',
        gap: 'large',
        boxShadow:
          '0px 4px 15px 0px rgba(0, 0, 0, 0.10), 0px 10px 20px 0px rgba(0, 0, 0, 0.05)',
        borderEndEndRadius: 'smallBottomRight',
        borderEndStartRadius: 'smallTopRight',
        borderStartStartRadius: 'smallTopLeft',
        borderStartEndRadius: 'smallBottomLeft',
        px: 'medium',
        py: 'small',
        marginBottom: 'medium',
      })}
    >
      <div className={css({ display: 'flex', gap: 'small' })}>
        <div
          className={css({
            padding: 'extra_small',
            border: '1px solid',
            borderColor: 'positive.stroke_primary',
            borderRadius: 'extra_small',
            minWidth: 'extra_large_2',
            height: 'extra_large_2',
          })}
        >
          <RegularClockPlusOutlineAlt size={20} />
        </div>

        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'extra_small_3',
          })}
        >
          <span
            className={css({
              fontSize: 'title.medium_medium',
              fontWeight: 'title.medium_medium',
              letterSpacing: 'title.medium_medium',
              lineHeight: 'title.medium_medium',
              color: 'neutrals.text_primary',
            })}
          >
            {translations.setUpSavingsPlanBoxTitle}
          </span>

          <span
            className={css({
              fontSize: 'caption.small_light',
              fontWeight: 'caption.small_light',
              letterSpacing: 'caption.small_light',
              lineHeight: 'caption.small_light',
              color: 'neutrals.text_secondary',
            })}
          >
            {translations.setUpSavingsPlanBoxDescription}
          </span>
        </div>
      </div>

      <ul>
        <li
          className={css({
            borderBottom: '1px solid',
            borderBottomColor: 'neutrals.divider',
          })}
        >
          {showFrequencyDropdown ? (
            <button
              type="button"
              onClick={handleFrequencyRowClick}
              className={css({ width: '100%', textAlign: 'left' })}
            >
              <SavingsPlanDateConfigItem
                label={translations.frequency}
                value={currentFrequencyLabel}
                hasIndicator
              />
            </button>
          ) : (
            <SavingsPlanDateConfigItem
              label={translations.frequency}
              value={translations.monthly}
            />
          )}
        </li>

        {frequencyValue !== SP_DAILY_FREQUENCY && (
          <li
            className={css({
              '& [data-element="date-picker-open-trigger"]': {
                width: '100%',
              },
            })}
          >
            {frequencyValue === SP_WEEKLY_FREQUENCY ||
            frequencyValue === SP_BIWEEKLY_FREQUENCY ? (
              <WeekDayPicker
                value={recurrenceDate}
                onChange={onChangeDate}
                cancelLabel={translations.cancel}
                confirmLabel={translations.ok}
                locale={locale}
              >
                <SavingsPlanDateConfigItem
                  label={translations.startsAt}
                  value={getWeekDayNameFromYmd(recurrenceDate, locale)}
                  hasIndicator
                />
              </WeekDayPicker>
            ) : (
              <DatePicker
                value={recurrenceDate}
                onChange={onChangeDate}
                cancelLabel={translations.cancel}
                confirmLabel={translations.ok}
                locale={locale}
                disabled={{
                  before: new Date(
                    getYmdForNthDayFromToday(
                      minimumCalendarDaysBeforeExecution,
                    ),
                  ),
                  after: new Date(
                    getYmdForCurrentMonthLengthAndNthDays(
                      maxDaysFromMonthEndForSavingsExecution,
                    ),
                  ),
                }}
              >
                <SavingsPlanDateConfigItem
                  label={translations.startsAt}
                  value={formatDateToYmd(
                    parse(recurrenceDate, YMD_STORAGE_FORMAT, new Date()),
                    {
                      locale,
                      separator: '-',
                    },
                  )}
                  hasIndicator
                />
              </DatePicker>
            )}
          </li>
        )}
      </ul>
    </div>
  );
};
